/**
 * document-vat-register.ts — Dokumenty: RĘCZNY rejestr VAT faktury (increment 1, model kliencki).
 *
 * Rejestr VAT to zestaw wierszy dopisywanych RĘCZNIE do faktury (zakupu/sprzedaży) —
 * osobny od pozycji dokumentu (`document_items`, patrz `document-items.ts`). Backend
 * (GET/POST/PATCH faktury) już istnieje — ten plik trzyma WYŁĄCZNIE typy klienta +
 * mapowania wiersz-z-serwera ↔ draft edytora ↔ payload do wysyłki, mirror wzorca
 * `document-items.ts` / `document-items-draft.ts`.
 *
 * Kwoty i stawka VAT jako STRINGI DZIESIĘTNE (konwencja `document_items`, zero utraty
 * precyzji przy transporcie JSON) — `valueVat`/`valueGross` są WSPÓŁLICZONE klientowo
 * (netto→VAT→brutto) przy zmianie netto/stawki, ale zostają ZWYKŁYMI polami
 * edytowalnymi (rejestr RĘCZNY — user może nadpisać wyliczoną wartość, np. przy
 * rozbieżności z fakturą źródłową).
 *
 * `lineNumber` renumeruje SERWER przy zapisie (mirror pozycji dokumentu) — klient
 * nigdy go nie wysyła w payloadzie, tylko czyta z GET / liczy do wyświetlenia
 * (indeks 1..N w kolejności `value`).
 *
 * Wpięcie na strony faktur i do rejestru layoutu (blok konfigurowalny) jest POZA
 * zakresem tego increment — kolumny są dziś STAŁE (kolejność WF), bez `columnConfig`
 * (w odróżnieniu od `document-items.ts`, które ma otwarty model kolumn dla ext-fields).
 */

import type { TFunction } from 'i18next';

import type { DictionaryItem } from '@/hooks/useDictionary';

/** Kategoria ABCD ewidencji VAT (JPK_V7) — `null` = brak przypisania. */
export type VatRegisterCategoryAbcd = 'A' | 'B' | 'C' | 'D';

/** Wariant kategorii w draft edytora — `''` reprezentuje `null` (natywny `<select>` wymaga stringa). */
export type VatRegisterCategoryAbcdDraft = VatRegisterCategoryAbcd | '';

/** Pojedynczy wiersz rejestru VAT z odpowiedzi GET faktury. */
export interface DocumentVatRegisterRow {
  id: string;
  lineNumber: number;
  registerCode: string;
  categoryAbcd: VatRegisterCategoryAbcd | null;
  vatRateCode: string;
  vatRate: string;
  valueNet: string;
  valueVat: string;
  valueGross: string;
  /** Te same kwoty W WALUCIE dokumentu; `null` = dokument złotówkowy (migracja 0158). */
  valueNetCurrency: string | null;
  valueVatCurrency: string | null;
  valueGrossCurrency: string | null;
  /** Znacznik „Usługa" (Symfonia `Service`) — `false` = towar. */
  service: boolean;
  /** Kategoria podatkowa — rubryka podsumowania VAT z FA(3); `null` = nieprzypisana. */
  taxCategory: string | null;
  /** YYYY-MM-DD, `null` = brak przypisanego okresu. */
  period: string | null;
  description: string | null;
  ue: boolean;
  gtuCodes: string[] | null;
}

/**
 * Kształt wiersza WYSYŁANY w tablicy `vatRegister` w POST/PATCH faktury. `id`/
 * `lineNumber` NIE są tu obecne — klient ich nie wysyła (serwer renumeruje przy
 * zapisie, `id` istniejącego wiersza serwer dopasowuje po pozycji w tablicy/replace-set,
 * nie po kliencie).
 */
export interface DocumentVatRegisterRowPayload {
  registerCode: string;
  categoryAbcd: VatRegisterCategoryAbcd | null;
  vatRateCode: string;
  vatRate: string;
  valueNet: string;
  valueVat: string;
  valueGross: string;
  valueNetCurrency: string | null;
  valueVatCurrency: string | null;
  valueGrossCurrency: string | null;
  service: boolean;
  taxCategory: string | null;
  period: string | null;
  description: string | null;
  ue: boolean;
  gtuCodes: string[] | null;
}

/**
 * Edytowalny wiersz rejestru VAT (stan grida). Pola nullable z kontraktu API są
 * tu STRINGAMI/TABLICĄ pustą (`''`/`[]` = brak wartości) — mirror konwencji
 * `DocumentLineItemDraft` (`document-items.ts`). `key` to stabilny identyfikator
 * klienta (React key) — dla wiersza wczytanego z serwera = `id`, dla nowego wiersza
 * = `crypto.randomUUID()`; NIGDY wysyłany do API.
 */
export interface DocumentVatRegisterRowDraft {
  key: string;
  registerCode: string;
  categoryAbcd: VatRegisterCategoryAbcdDraft;
  vatRateCode: string;
  vatRate: string;
  valueNet: string;
  valueVat: string;
  valueGross: string;
  /** Kwoty w walucie — w drafcie `''` znaczy „nie podano" (mirror konwencji pozostałych pól). */
  valueNetCurrency: string;
  valueVatCurrency: string;
  valueGrossCurrency: string;
  service: boolean;
  /** `''` = kategoria podatkowa nieprzypisana (natywny wybór wymaga stringa). */
  taxCategory: string;
  period: string;
  description: string;
  ue: boolean;
  gtuCodes: string[];
}

/** Pozycja z odpowiedzi GET → draft edytowalny. */
export function serverVatRegisterRowToDraft(
  row: DocumentVatRegisterRow,
): DocumentVatRegisterRowDraft {
  return {
    key: row.id,
    registerCode: row.registerCode,
    categoryAbcd: row.categoryAbcd ?? '',
    vatRateCode: row.vatRateCode,
    vatRate: row.vatRate,
    valueNet: row.valueNet,
    valueVat: row.valueVat,
    valueGross: row.valueGross,
    valueNetCurrency: row.valueNetCurrency ?? '',
    valueVatCurrency: row.valueVatCurrency ?? '',
    valueGrossCurrency: row.valueGrossCurrency ?? '',
    service: row.service,
    taxCategory: row.taxCategory ?? '',
    period: row.period ?? '',
    description: row.description ?? '',
    ue: row.ue,
    gtuCodes: row.gtuCodes ?? [],
  };
}

/** Draft → kształt wysyłany do API. */
export function vatRegisterDraftToPayload(
  d: DocumentVatRegisterRowDraft,
): DocumentVatRegisterRowPayload {
  return {
    registerCode: d.registerCode.trim(),
    categoryAbcd: d.categoryAbcd === '' ? null : d.categoryAbcd,
    vatRateCode: d.vatRateCode.trim(),
    vatRate: d.vatRate.trim(),
    valueNet: d.valueNet.trim(),
    valueVat: d.valueVat.trim(),
    valueGross: d.valueGross.trim(),
    // Puste pole walutowe → `null`, nie `'0'`: „nie podano" i „w walucie wychodzi zero" to
    // dwa różne stany, a walidator backendu przepuszcza tylko liczbę albo null.
    valueNetCurrency: d.valueNetCurrency.trim() || null,
    valueVatCurrency: d.valueVatCurrency.trim() || null,
    valueGrossCurrency: d.valueGrossCurrency.trim() || null,
    service: d.service,
    taxCategory: d.taxCategory.trim() || null,
    period: d.period.trim() || null,
    description: d.description.trim() || null,
    ue: d.ue,
    gtuCodes: d.gtuCodes.length > 0 ? d.gtuCodes : null,
  };
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Przelicza netto→VAT→brutto (PODGLĄD klientowy, nie silnik serwerowy — rejestr
 * jest zapisem RĘCZNYM, backend nie przelicza tych wierszy przy zapisie, więc tu
 * NIE ma rozbieżności preview-vs-prawda jak w `document-items-draft.ts`).
 *
 * `valueNet` niepusty i nieparsowalny do liczby skończonej → `{ valueVat: '', valueGross: '' }`
 * (nie cichy `NaN`/`'0'` — wołający zostawia pola puste, user widzi że nic nie policzono).
 * Stawka ≤0 / niepoliczalna (np. pozycja słownika „zw"/„np"/„oo" z `value===null` → `vatRate`
 * mapuje się na `'0'` po stronie wołającego, patrz `DocumentVatRegisterEditor.onVatRateChange`)
 * → `vat=0`, `gross=net` (zero mnożenia przez ujemną/NaN stawkę).
 */
export function computeVatAndGrossFromNet(
  valueNet: string,
  vatRate: string,
): { valueVat: string; valueGross: string } {
  const net = Number(valueNet);
  if (valueNet.trim() === '' || !Number.isFinite(net)) return { valueVat: '', valueGross: '' };
  const rateNum = Number(vatRate);
  const rate = Number.isFinite(rateNum) && rateNum > 0 ? rateNum : 0;
  const vat = rate > 0 ? round2(net * (rate / 100)) : 0;
  const gross = round2(net + vat);
  return { valueVat: vat.toFixed(2), valueGross: gross.toFixed(2) };
}

/**
 * Nowy pusty wiersz — „reszta netto do rozbicia" (decyzja UX, patrz nagłówek zadania):
 * `valueNet` startuje od (żywe netto nagłówka faktury `amountNet`) MINUS suma `valueNet`
 * już wpisanych wierszy rejestru. `amountNet` nieznane (`null`/pusty/nieparsowalny) →
 * `valueNet` zostaje pusty (nic do policzenia). Reszta różnicy MOŻE wyjść ujemna (user
 * już rozpisał więcej niż netto dokumentu) — świadomie NIE klamrowane do zera, to tylko
 * podpowiedź do dalszej edycji, nie walidacja.
 *
 * Domyślna kategoria `'A'` (najczęstszy przypadek) — reszta pól pusta. BRAK podpowiedzi
 * rejestr→stawka (świadomie odłożone, patrz zadanie) — wybór rejestru nie podstawia stawki.
 */
export function emptyVatRegisterRow(
  amountNet: string | null | undefined,
  existingRows: readonly DocumentVatRegisterRowDraft[],
): DocumentVatRegisterRowDraft {
  const headerNet = amountNet != null && amountNet.trim() !== '' ? Number(amountNet) : null;
  const valueNet =
    headerNet != null && Number.isFinite(headerNet)
      ? round2(
          headerNet - existingRows.reduce((acc, row) => acc + (Number(row.valueNet) || 0), 0),
        ).toFixed(2)
      : '';
  return {
    key: crypto.randomUUID(),
    registerCode: '',
    categoryAbcd: 'A',
    vatRateCode: '',
    vatRate: '',
    valueNet,
    valueVat: '',
    valueGross: '',
    valueNetCurrency: '',
    valueVatCurrency: '',
    valueGrossCurrency: '',
    service: false,
    taxCategory: '',
    period: '',
    description: '',
    ue: false,
    gtuCodes: [],
  };
}

/**
 * Dopasowuje kod stawki VAT do pozycji słownika `vat_rates` (PEŁNA lista — mirror
 * `document-items-draft.ts`: kod wczytanego wiersza może wskazywać pozycję, która w
 * międzyczasie przestała być aktywna/selectable, a mimo to ma poprawną wartość liczbową
 * do przeliczeń) i zwraca `vatRate` do zapisania w drafcie. Pozycja bez wartości liczbowej
 * (`value===null`, np. „zw"/„np"/„oo") → `'0'`, nie pusty string (spójne z `draftFromProduct`).
 */
export function resolveVatRateValue(
  vatRateCode: string,
  vatAllItems: readonly DictionaryItem[],
): string {
  const match = vatAllItems.find((item) => item.code === vatRateCode);
  return normalizeVatRateForColumn(match?.value ?? '0');
}

/**
 * Stawka ze słownika → wartość mieszcząca się w kolumnie `vat_rate numeric(5,2)`.
 *
 * Pozycje słownika `vat_rates` trzymają `value` z szeroką precyzją (realnie `23.000000` —
 * kolumna `dictionary_items.value` ma 6 miejsc), a walidator payloadu rejestru VAT **świadomie
 * odrzuca** więcej niż 2 miejsca po przecinku, żeby Postgres nie zaokrąglał po cichu. Wysłanie
 * wartości ze słownika 1:1 kończyło się więc 400 na polu `vatRate` przy KAŻDYM wierszu ze
 * stawką wybraną z listy — z generycznym komunikatem „sprawdź wymagane pola", bo błąd siedzi
 * w zagnieżdżonej tablicy (znalezione 7.09 na realnym tenancie).
 *
 * Zaokrąglamy do 2 miejsc (dokładnie to, co i tak zrobiłaby kolumna) i obcinamy zbędne zera,
 * więc `23.000000` → `23`, a `8.500000` → `8.5`. Wartość niepoliczalna → `'0'` (spójnie z
 * `computeVatAndGrossFromNet`, gdzie stawka ≤0 daje VAT 0).
 */
export function normalizeVatRateForColumn(raw: string | null | undefined): string {
  const n = Number(raw);
  if (raw == null || String(raw).trim() === '' || !Number.isFinite(n) || n < 0) return '0';
  return String(Math.round(n * 100) / 100);
}

/**
 * Etykieta kategorii podatkowej (rubryka FA(3)) — `t()` z LITERAŁEM per gałąź, nie szablonem
 * ze zmienną: klucze budowane dynamicznie są martwym polem skanera `check:i18n`
 * (ten sam powód co w `accountSegmentLabel`/`behindPostingDescription`). NIE refaktoryzować
 * na lookup po kodzie.
 */
export function vatTaxCategoryLabel(code: string, t: TFunction): string {
  switch (code) {
    case 'podstawowa':
      return t('tenant.documents.vatRegister.taxCategory.podstawowa');
    case 'obnizona_1':
      return t('tenant.documents.vatRegister.taxCategory.obnizona_1');
    case 'obnizona_2':
      return t('tenant.documents.vatRegister.taxCategory.obnizona_2');
    case 'taksowki':
      return t('tenant.documents.vatRegister.taxCategory.taksowki');
    case 'oss':
      return t('tenant.documents.vatRegister.taxCategory.oss');
    case 'zero_krajowa':
      return t('tenant.documents.vatRegister.taxCategory.zero_krajowa');
    case 'zero_wdt':
      return t('tenant.documents.vatRegister.taxCategory.zero_wdt');
    case 'zero_eksport':
      return t('tenant.documents.vatRegister.taxCategory.zero_eksport');
    case 'zwolniona':
      return t('tenant.documents.vatRegister.taxCategory.zwolniona');
    case 'poza_krajem':
      return t('tenant.documents.vatRegister.taxCategory.poza_krajem');
    case 'uslugi_art100':
      return t('tenant.documents.vatRegister.taxCategory.uslugi_art100');
    case 'oo_krajowe':
      return t('tenant.documents.vatRegister.taxCategory.oo_krajowe');
    case 'marza':
      return t('tenant.documents.vatRegister.taxCategory.marza');
    default:
      // Kod z serwera spoza znanego zbioru (np. po ręcznej korekcie w bazie) — pokazujemy
      // surową wartość, zamiast udawać, że pola nie ma.
      return code;
  }
}

/** Identyfikatory kolumn tabeli rejestru VAT — STAŁE (kolejność WF), bez modelu ext-fields/columnConfig. */
export type DocumentVatRegisterColumnId =
  | 'lineNumber'
  | 'registerCode'
  | 'categoryAbcd'
  | 'taxCategory'
  | 'vatRateCode'
  | 'valueNet'
  | 'valueVat'
  | 'valueGross'
  | 'valueNetCurrency'
  | 'valueVatCurrency'
  | 'valueGrossCurrency'
  | 'service'
  | 'period'
  | 'ue'
  | 'gtuCodes'
  | 'actions';

/**
 * Pełny zestaw kolumn edytora, w kolejności: `Lp.` → rejestr → kategorie → stawka → kwoty w PLN →
 * te same kwoty w walucie → znaczniki → okres → reszta. Kwoty walutowe stoją OBOK złotówkowych
 * (nie na końcu tabeli), bo księgowa czyta je parami. `Lp.` jest PIERWSZE: skoro numerujemy
 * wiersze, numer jest tym, po czym się je wskazuje, a nie doklejką na końcu.
 *
 * Kolumna „Opis" ZDJĘTA (niepotrzebna — decyzja z 7.09). Pole `description` zostaje w modelu
 * wiersza i w payloadzie jako PRZELOTKA: zapis jest replace-setem całego kompletu, więc
 * wyrzucenie pola z drafta wyczyściłoby opisy zapisane wcześniej. Kolumnę w bazie da się zdjąć
 * osobną migracją, gdy będzie pewność, że nikt tych opisów nie potrzebuje.
 */
export const VAT_REGISTER_EDITOR_COLUMN_IDS: readonly DocumentVatRegisterColumnId[] = [
  'lineNumber',
  'registerCode',
  'categoryAbcd',
  'taxCategory',
  'vatRateCode',
  'valueNet',
  'valueVat',
  'valueGross',
  'valueNetCurrency',
  'valueVatCurrency',
  'valueGrossCurrency',
  'service',
  'period',
  'ue',
  'gtuCodes',
  'actions',
];

/** Podzbiór widoku odczytu — wszystko poza kolumną akcji edytora. */
export const VAT_REGISTER_SECTION_COLUMN_IDS: readonly DocumentVatRegisterColumnId[] =
  VAT_REGISTER_EDITOR_COLUMN_IDS.filter((id) => id !== 'actions');

/** Klucz i18n nagłówka kolumny — współdzielony przez edytor i widok odczytu. */
export const VAT_REGISTER_COLUMN_HEADER_KEYS: Record<DocumentVatRegisterColumnId, string> = {
  lineNumber: 'tenant.documents.vatRegister.columns.lineNumber',
  registerCode: 'tenant.documents.vatRegister.columns.registerCode',
  categoryAbcd: 'tenant.documents.vatRegister.columns.categoryAbcd',
  taxCategory: 'tenant.documents.vatRegister.columns.taxCategory',
  vatRateCode: 'tenant.documents.vatRegister.columns.vatRateCode',
  valueNet: 'tenant.documents.vatRegister.columns.valueNet',
  valueVat: 'tenant.documents.vatRegister.columns.valueVat',
  valueGross: 'tenant.documents.vatRegister.columns.valueGross',
  valueNetCurrency: 'tenant.documents.vatRegister.columns.valueNetCurrency',
  valueVatCurrency: 'tenant.documents.vatRegister.columns.valueVatCurrency',
  valueGrossCurrency: 'tenant.documents.vatRegister.columns.valueGrossCurrency',
  service: 'tenant.documents.vatRegister.columns.service',
  period: 'tenant.documents.vatRegister.columns.period',
  ue: 'tenant.documents.vatRegister.columns.ue',
  gtuCodes: 'tenant.documents.vatRegister.columns.gtuCodes',
  actions: 'tenant.documents.vatRegister.editor.actions',
};
